import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, Link, useNavigate } from 'react-router-dom';
import { CalendarDays, Edit3, ExternalLink, LogOut, Plus, QrCode, Trash2, X } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import { clearToken, createCalendarEvent, deleteCalendarEvent, getToken, listAdminCalendarEvents, me, updateCalendarEvent } from '../lib/api';
import { useToast } from '../hooks/use-toast';
import { iceLogo } from '../mock';

const EMPTY_EVENT = { title: '', dateTime: '', location: '', description: '' };

export default function AdminEvents() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [access, setAccess] = useState('checking');
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY_EVENT);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => setEvents(await listAdminCalendarEvents()), []);

  useEffect(() => {
    if (!getToken()) { setAccess('signed-out'); return; }
    me().then((profile) => {
      setUser(profile);
      setAccess(profile.roles?.includes('event_editor') ? 'allowed' : 'denied');
    }).catch(() => { clearToken(); setAccess('signed-out'); });
  }, []);

  useEffect(() => { if (access === 'allowed') load().catch(() => setEvents([])); }, [access, load]);

  if (access === 'signed-out') return <Navigate to="/admin/login" replace />;
  if (access === 'checking') return <div className="min-h-screen bg-[#0a1f44]" data-testid="calendar-admin-checking" />;

  const startCreate = () => { setEditingId(null); setForm(EMPTY_EVENT); setShowForm(true); };
  const startEdit = (event) => {
    setEditingId(event.id);
    setForm({ title: event.title, dateTime: event.dateTime, location: event.location, description: event.description });
    setShowForm(true);
  };

  const save = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      if (editingId) await updateCalendarEvent(editingId, form);
      else await createCalendarEvent(form);
      await load();
      setShowForm(false);
      setForm(EMPTY_EVENT);
      setEditingId(null);
      toast({ title: editingId ? 'Event updated' : 'Event added to calendar' });
    } catch (error) {
      toast({ title: 'Could not save event', description: error.response?.data?.detail || error.message });
    } finally { setSaving(false); }
  };

  const remove = async (event) => {
    if (!window.confirm(`Delete “${event.title}” permanently?`)) return;
    await deleteCalendarEvent(event.id);
    await load();
    toast({ title: 'Event deleted' });
  };

  const calendarUrl = typeof window !== 'undefined' ? `${window.location.origin}/events#calendar` : '/events#calendar';
  const active = events.filter((event) => !event.isArchived);
  const archived = events.filter((event) => event.isArchived);

  return (
    <div className="min-h-screen bg-[#f5f6f8] font-body">
      <header className="bg-[#0a1f44] text-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src={iceLogo} alt="ICE" className="h-10 w-auto" />
            <div><div className="font-display text-xl">BOARD CALENDAR</div><div className="font-heading uppercase tracking-widest text-[10px] text-[#c8102e]">{user?.name}</div></div>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/football-practice-times" data-testid="calendar-admin-practice-times-link" className="inline-flex items-center gap-2 border border-white/30 px-4 py-2 font-heading uppercase tracking-widest text-xs hover:bg-white hover:text-[#0a1f44] transition-colors">Practice Times</Link>
            <Link to="/events#calendar" data-testid="calendar-admin-public-link" className="inline-flex items-center gap-2 border border-white/30 px-4 py-2 font-heading uppercase tracking-widest text-xs hover:bg-white hover:text-[#0a1f44] transition-colors"><ExternalLink className="w-4 h-4" /> Public View</Link>
            <Link to="/admin/account" data-testid="admin-nav-account" className="border border-white/30 px-4 py-2 font-heading uppercase tracking-widest text-xs hover:bg-white hover:text-[#0a1f44] transition-colors">Change Password</Link>
            <button onClick={() => { clearToken(); nav('/admin/login'); }} data-testid="calendar-admin-logout" className="inline-flex items-center gap-2 bg-[#c8102e] px-4 py-2 font-heading uppercase tracking-widest text-xs"><LogOut className="w-4 h-4" /> Sign Out</button>
          </div>
        </div>
      </header>

      {access === 'denied' ? (
        <main className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="calendar-admin-denied">
          <CalendarDays className="w-12 h-12 text-[#c8102e] mx-auto mb-5" />
          <h1 className="font-display text-4xl text-[#0a1f44]">CALENDAR ACCESS LOCKED</h1>
          <p className="text-[#0a1f44]/70 mt-3">Only the Vice President and Secretary can add or edit calendar events.</p>
        </main>
      ) : (
        <main className="max-w-7xl mx-auto px-6 py-10 space-y-10">
          <section className="grid lg:grid-cols-[1fr_260px] gap-8 items-start">
            <div>
              <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
                <div><div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-2">VP &amp; Secretary Access</div><h1 className="font-display text-4xl md:text-5xl text-[#0a1f44]">MANAGE EVENTS</h1></div>
                <button type="button" onClick={startCreate} data-testid="calendar-admin-add" className="inline-flex items-center gap-2 bg-[#c8102e] text-white px-5 py-3 font-heading uppercase tracking-widest text-xs hover:bg-[#e11d34] transition-colors"><Plus className="w-4 h-4" /> Add Event</button>
              </div>
              <p className="text-sm text-[#0a1f44]/65 max-w-2xl">Events publish immediately. They leave the public calendar 24 hours after their scheduled time and remain below under Archived.</p>
            </div>
            <aside className="bg-white border border-[#0a1f44]/15 p-5 text-center" data-testid="calendar-admin-qr">
              <QrCode className="w-6 h-6 text-[#c8102e] mx-auto mb-2" /><div className="font-display text-xl text-[#0a1f44]">PUBLIC CALENDAR QR</div>
              <div className="inline-block border border-[#0a1f44] p-2 mt-4"><QRCodeCanvas value={calendarUrl} size={150} bgColor="#ffffff" fgColor="#0a1f44" level="M" /></div>
            </aside>
          </section>

          {showForm && (
            <form onSubmit={save} className="bg-white border-t-4 border-[#c8102e] p-6 md:p-8 grid md:grid-cols-2 gap-5" data-testid="calendar-event-form">
              <div className="md:col-span-2 flex items-center justify-between"><h2 className="font-display text-3xl text-[#0a1f44]">{editingId ? 'EDIT EVENT' : 'ADD EVENT'}</h2><button type="button" onClick={() => setShowForm(false)} data-testid="calendar-form-close" aria-label="Close event form"><X className="w-5 h-5" /></button></div>
              <label className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70">Title *<input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="calendar-form-title" className="mt-2 block w-full border border-[#0a1f44]/20 p-3 text-base normal-case" /></label>
              <label className="text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70">Date &amp; Time *<input required type="datetime-local" value={form.dateTime} onChange={(e) => setForm({ ...form, dateTime: e.target.value })} data-testid="calendar-form-datetime" className="mt-2 block w-full border border-[#0a1f44]/20 p-3 text-base normal-case" /></label>
              <label className="md:col-span-2 text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70">Location<input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="calendar-form-location" className="mt-2 block w-full border border-[#0a1f44]/20 p-3 text-base normal-case" /></label>
              <label className="md:col-span-2 text-xs font-heading uppercase tracking-widest text-[#0a1f44]/70">Details<textarea rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="calendar-form-description" className="mt-2 block w-full border border-[#0a1f44]/20 p-3 text-base normal-case resize-none" /></label>
              <button type="submit" disabled={saving} data-testid="calendar-form-save" className="md:col-span-2 bg-[#0a1f44] hover:bg-[#c8102e] disabled:opacity-50 text-white py-3 font-heading uppercase tracking-widest text-xs transition-colors">{saving ? 'Saving…' : editingId ? 'Save Changes' : 'Publish Event'}</button>
            </form>
          )}

          <EventList title="Upcoming & Current" events={active} onEdit={startEdit} onDelete={remove} testid="calendar-active-events" />
          <EventList title="Archived · Publicly Hidden" events={archived} onEdit={startEdit} onDelete={remove} testid="calendar-archived-events" archived />
        </main>
      )}
    </div>
  );
}

const EventList = ({ title, events, onEdit, onDelete, testid, archived = false }) => (
  <section data-testid={testid}>
    <div className="flex items-center gap-3 mb-4"><h2 className="font-display text-3xl text-[#0a1f44]">{title.toUpperCase()}</h2><span className={`font-heading text-xs px-2 py-1 ${archived ? 'bg-[#0a1f44]/10 text-[#0a1f44]/60' : 'bg-emerald-100 text-emerald-800'}`}>{events.length}</span></div>
    {events.length === 0 ? <div className="border border-dashed border-[#0a1f44]/20 p-8 text-sm text-[#0a1f44]/60">No events in this section.</div> : (
      <div className="divide-y divide-[#0a1f44]/10 border border-[#0a1f44]/15 bg-white">
        {events.map((event) => <div key={event.id} data-testid={`calendar-admin-event-${event.id}`} className="p-5 flex flex-col md:flex-row md:items-center gap-4"><div className="flex-1 min-w-0"><div className="font-display text-2xl text-[#0a1f44]">{event.title.toUpperCase()}</div><div className="text-xs text-[#0a1f44]/60 mt-1">{new Date(event.dateTime).toLocaleString()} · {event.location || 'Location TBA'} · by {event.contributorName}</div></div><div className="flex gap-2"><button type="button" onClick={() => onEdit(event)} data-testid={`calendar-edit-${event.id}`} aria-label={`Edit ${event.title}`} className="p-2 border border-[#0a1f44]/20 hover:border-[#c8102e] text-[#0a1f44]"><Edit3 className="w-4 h-4" /></button><button type="button" onClick={() => onDelete(event)} data-testid={`calendar-delete-${event.id}`} aria-label={`Delete ${event.title}`} className="p-2 border border-[#c8102e]/30 hover:bg-[#c8102e] hover:text-white text-[#c8102e]"><Trash2 className="w-4 h-4" /></button></div></div>)}
      </div>
    )}
  </section>
);